/**
 * ================================================================================================
 *  TABS ON A RECORD CARD — one subject at a time, and the address remembers which. **2026-10-25.**
 * ================================================================================================
 *
 * Owner, looking at the reference company card he sent:
 *
 *     *"תסתכל על מסך ההגדרות עם הטאבים הנלווים, לא אומר שזה המלל שצריך אלא המבנה"*
 *     *"כרטיס החברה … טאבים לפי נושא וקבוצות שדות מתקפלות … אפשר לשמור ולהמשיך בהמשך"*
 *
 * The company card was eight stacked blocks — a settings report, an accounting-system picker, a
 * fact list, three link buttons and three long forms — all rendered at once, so finishing one field
 * meant scrolling past the two that follow it. Tabs are not decoration here: they are what makes
 * *"save and come back later"* a thing a person can actually do, because coming back lands on the
 * part they were in.
 *
 * ── THE SELECTED TAB LIVES IN THE ADDRESS, NOT ONLY IN STATE ────────────────────────────────────
 *
 * `#connection` on the URL. That is what makes the choice survive a reload and makes a tab
 * linkable — *"the connection step of this company"* is a thing support can send somebody to. It is
 * written with `replaceState`, so the browser Back button still leaves the card rather than walking
 * back through the tabs somebody clicked.
 *
 * `window` is read defensively. These screens are rendered in jsdom by the suites and in a harness
 * that mounts them without a router, and a tab strip is not worth a crash in either.
 *
 * ── IT IS A REAL TABLIST ────────────────────────────────────────────────────────────────────────
 *
 * `role="tab"` with `aria-selected` and `aria-controls`, arrow keys moving between tabs, and only
 * the selected tab in the page tab order (`tabIndex={-1}` on the rest) — which is the documented
 * keyboard behaviour for this pattern and the reason it is built from buttons rather than links.
 * A link would navigate; this stays on the record.
 *
 * ── WHAT IS NOT HIDDEN BEHIND A TAB ─────────────────────────────────────────────────────────────
 *
 * The record's HEADER — its name, its official number, its state — stays fixed above the strip, and
 * anything that reports a problem with the record stays with it. A warning that only appears when
 * you happen to open the right tab is a warning the product cannot claim it gave.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export interface TabDefinition {
  /** Stable, ASCII, and used in the address. Never translated. */
  readonly id: string;
  readonly labelHe: string;
  /** Rendered only while this tab is the selected one. */
  readonly render: () => ReactNode;
  /** `true` ⇒ a small mark on the tab, for a step that is not done. Never a colour alone. */
  readonly outstanding?: boolean;
}

export interface TabStripProps {
  /** Distinguishes two strips on one page, and namespaces the ids the ARIA attributes use. */
  readonly name: string;
  readonly tabs: readonly TabDefinition[];
  readonly ariaLabelHe: string;
}

function hashTab(tabs: readonly TabDefinition[]): string | null {
  if (typeof window === 'undefined') return null;
  const fromHash = window.location.hash.replace(/^#/u, '');
  return tabs.some((tab) => tab.id === fromHash) ? fromHash : null;
}

export function TabStrip({ name, tabs, ariaLabelHe }: TabStripProps): JSX.Element | null {
  const first = tabs[0];
  const [selected, setSelected] = useState<string>(() => hashTab(tabs) ?? first?.id ?? '');

  /*
   * The address is written in an effect rather than in the click handler, so a tab chosen by any
   * route — a hash the page was opened on, a programmatic change — ends up in the same place. The
   * guard stops an initial render from pushing a hash onto an address that had none.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || selected === '') return;
    if (window.location.hash.replace(/^#/u, '') === selected) return;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${selected}`);
  }, [selected]);

  /*
   * `delta` is LOGICAL — +1 is the next tab — and the arrow keys are mapped through the document's
   * direction. In Hebrew the next tab is to the LEFT of the current one, and a strip that moved the
   * other way would be the one place in the product where an arrow key disagrees with the eye.
   */
  const move = useCallback(
    (from: string, delta: number): void => {
      const index = tabs.findIndex((tab) => tab.id === from);
      if (index === -1) return;
      // Wraps, which is what this pattern does: from the last tab, forward reaches the first.
      const next = tabs[(index + delta + tabs.length) % tabs.length];
      if (next === undefined) return;
      setSelected(next.id);
      if (typeof document === 'undefined') return;
      const el = document.getElementById(`${name}-tab-${next.id}`);
      if (el !== null) el.focus();
    },
    [name, tabs],
  );

  if (first === undefined) return null;
  const active = tabs.find((tab) => tab.id === selected) ?? first;

  return (
    <div className="tabstrip">
      <div className="tabstrip__list" role="tablist" aria-label={ariaLabelHe}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`${name}-tab-${tab.id}`}
            type="button"
            role="tab"
            className="tabstrip__tab"
            aria-selected={tab.id === active.id}
            aria-controls={`${name}-panel-${tab.id}`}
            // Only the selected tab is in the page tab order; the arrows move within the strip.
            tabIndex={tab.id === active.id ? 0 : -1}
            onClick={() => setSelected(tab.id)}
            onKeyDown={(e) => {
              const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
              if (e.key === 'ArrowLeft') move(tab.id, rtl ? 1 : -1);
              if (e.key === 'ArrowRight') move(tab.id, rtl ? -1 : 1);
            }}
          >
            {tab.labelHe}
            {/*
              A WORD, not a dot. 09 §9.5 A3: colour is never the only carrier of meaning, and a
              coloured dot on a tab is exactly that. The text is short enough to live on the tab.
            */}
            {tab.outstanding === true ? <span className="tabstrip__flag">להשלמה</span> : null}
          </button>
        ))}
      </div>
      <div
        id={`${name}-panel-${active.id}`}
        role="tabpanel"
        aria-labelledby={`${name}-tab-${active.id}`}
        className="tabstrip__panel"
        tabIndex={0}
      >
        {active.render()}
      </div>
    </div>
  );
}
